import type { Me } from '#auth/composables/useAuth'
import { MemberStatusEnum } from '@forgd/shared/enums'
import { projectLandingPath } from '../../../utils/project-paths'

const onboardingPath = process.env.ONBOARDING_PATH || '/onboarding'

/**
 * Onboarding scenarios enum representing the different user states and their redirect destinations
 */
export enum OnboardingScenario {
  OWNER_ACTIVE_NO_PROJECTS = 1, // Owner with active org, no projects → owner onboarding
  OWNER_ACTIVE_WITH_PROJECTS = 2, // Owner with active org, has projects → project home
  INVITED_ACTIVE_NO_PROJECTS = 3, // Invited user with active org, no projects → teammate onboarding
  INVITED_ACTIVE_WITH_PROJECTS = 4, // Invited user with active org, has projects → project home
  QUERY_ORGANIZATION_ID = 5, // Route query has organizationId → teammate onboarding
  INVITED_PENDING_INVITATION = 6, // User with pending invitation → teammate onboarding
}

/**
 * There are 6 scenarios:
 * 1. User has OWN memberStatus=active organization with no onboarded projects (no projects at all) → redirect to owner onboarding
 * 2. User has OWN memberStatus=active organization with an onboarded project → redirect to project home
 * 3. User has INVITED memberStatus=active organization with no onboarded projects → redirect to teammate onboarding with organizationId
 * 4. User has INVITED memberStatus=active organization with an onboarded project → redirect to project home
 * 5. There's route.query.organizationId → redirect to teammate onboarding with organizationId
 * 6. User has INVITED memberStatus=invited organization → redirect to teammate onboarding with organizationId
 */

export function inferOnboardingState(me: Me) {
  const activeOrganizationWithOnboardedProject = me.organizations.find(
    org => org.memberStatus === MemberStatusEnum.Active && org.projects.length,
  )

  // case when we need to redirect to teammate onboarding with organizationId (6).
  // Only fires for users with no active workspace to land in — for established
  // users the pending invitation surfaces in the account dropdown instead, so
  // the onboarding guard doesn't hijack their navigation.
  const invitedOrganization = me.organizations.find(
    org => org.memberStatus === MemberStatusEnum.Invited && org.ownerUserId !== me.id,
  )
  if (invitedOrganization && !activeOrganizationWithOnboardedProject) {
    return {
      scenario: OnboardingScenario.INVITED_PENDING_INVITATION,
      redirectTo: `${onboardingPath}/${invitedOrganization.id}/teammate`,
    }
  }

  // case when we need to redirect to teammate onboarding with organizationId (3)
  const activeInvitedOrganization = me.organizations.find(
    org => org.memberStatus === MemberStatusEnum.Active && org.ownerUserId !== me.id && !org.projects.length,
  )
  if (activeInvitedOrganization && !activeOrganizationWithOnboardedProject) {
    return {
      scenario: OnboardingScenario.INVITED_ACTIVE_NO_PROJECTS,
      redirectTo: `${onboardingPath}/${activeInvitedOrganization.id}/teammate`,
    }
  }

  // case when we need to redirect to project home (2, 4)
  if (activeOrganizationWithOnboardedProject) {
    const selectedProject = inferSelectedProject(me)
    return {
      scenario: OnboardingScenario.OWNER_ACTIVE_WITH_PROJECTS,
      redirectTo: projectLandingPath(selectedProject!.id),
    }
  }

  // case when we need to redirect to owner onboarding (1)
  const activeOwnOrganizationWithoutOnboardedProjects = me.organizations.find(
    org => org.memberStatus === MemberStatusEnum.Active && org.ownerUserId === me.id && (!org.projects.length || org.projects.some(p => !p.isOnboarded)),
  )
  if (activeOwnOrganizationWithoutOnboardedProjects) {
    return {
      scenario: OnboardingScenario.OWNER_ACTIVE_NO_PROJECTS,
      redirectTo: onboardingPath,
    }
  }

  throw new Error('No onboarding state found')
}

export function inferSelectedProject(me: Me) {
  // #1 active project which they are an owner of
  const ownOrgWithProject = me.organizations.find(p => p.memberStatus === MemberStatusEnum.Active && p.ownerUserId === me.id && p.projects.length > 0)
  if (ownOrgWithProject) {
    return ownOrgWithProject.projects[0]
  }
  // #2 first active project they are a member of
  const anyOrgWithProject = me.organizations.find(p => p.memberStatus === MemberStatusEnum.Active && p.projects.length > 0)
  if (anyOrgWithProject) {
    return anyOrgWithProject.projects[0]
  }
  // #3 sounds like a job for onboarding
  return null
}
